/**
 * Subscription Packages slice for Redux store
 */

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchSubscriptionPackages } from '../services/subscriptionService';
import type { SubscriptionPackageInfo } from '../types/subscription';

interface SubscriptionPackagesState {
  packages: SubscriptionPackageInfo[];
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;
}

const initialState: SubscriptionPackagesState = {
  packages: [],
  isLoading: false,
  error: null,
  lastFetched: null,
};

/**
 * Thunk: Fetch subscription packages from Supabase
 */
export const getSubscriptionPackages = createAsyncThunk(
  'subscriptionPackages/fetch',
  async (_, { rejectWithValue }) => {
    const response = await fetchSubscriptionPackages();
    if (!response.success) {
      return rejectWithValue(response.error || 'Failed to fetch packages');
    }
    return response.data || [];
  },
);

const subscriptionPackagesSlice = createSlice({
  name: 'subscriptionPackages',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSubscriptionPackages.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        getSubscriptionPackages.fulfilled,
        (state, action: PayloadAction<SubscriptionPackageInfo[]>) => {
          state.isLoading = false;
          state.packages = action.payload;
          state.lastFetched = Date.now();
        },
      )
      .addCase(getSubscriptionPackages.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Failed to fetch packages';
      });
  },
});

export const { clearError } = subscriptionPackagesSlice.actions;

export default subscriptionPackagesSlice.reducer;
